/**
 * Apresentação das seis telas de frota. AQUI NÃO SE DECIDE NADA SOBRE DADO.
 *
 * Todo componente deste arquivo recebe estado JÁ CLASSIFICADO por
 * `aggregates.ts` e frase JÁ PRONTA. A única coisa que ele escolhe é cor,
 * opacidade e ícone — que é apresentação, não afirmação.
 *
 * A divisão existe pelo motivo registrado no módulo da Subestação: enquanto o
 * componente recebia o ponto cru, a revisão cruzada recompôs o valor a partir
 * dele TRÊS vezes, e o resultado é pior que o defeito original — a tela passa a
 * ATESTAR uma procedência que o número não tem. Aqui não sobra nada para
 * recompor: a célula chega como `CelulaDeCampo` e o JSX só a transporta.
 */
import type { ReactNode } from 'react';
import {
  ROTULO_DO_CAMPO,
  ROTULO_DO_MOVIMENTO,
  type CampoDeVeiculo,
  type CelulaDeCampo,
  type EstadoDeMovimento,
} from './aggregates.js';

type Tom = 'vivo' | 'neutro' | 'aviso' | 'perigo';

/**
 * A cor de cada tom. `vivo` NÃO tem cor própria: valor corrente herda a cor do
 * texto da tabela, e pintar de verde tudo que está fresco treina o operador a
 * ignorar verde — é o mesmo argumento do semáforo decorativo em `bands.ts`.
 */
const COR_DO_TOM: Record<Tom, string> = {
  vivo: 'var(--nx-ink)',
  neutro: 'var(--nx-mute)',
  /* `--nx-stale-ink` e não `--nx-warning`: o sufixo "(último)" é o PORTADOR
     NÃO-CROMÁTICO do padrão STALE, e reprovava a 2,15:1 no claro. Um portador
     que substitui a cor e depende de enxergar a cor não substitui nada. */
  aviso: 'var(--nx-stale-ink)',
  perigo: 'var(--nx-alert-ink)',
};

/**
 * O PADRÃO STALE, num lugar só — e ele DIVERGE DA LETRA DO `CLAUDE.md`.
 *
 * ---------------------------------------------------------------------------
 * A OPACIDADE SAIU DO VALOR. O `grayscale` FICOU.
 *
 * O `CLAUDE.md` prescreve `opacity: .55` + `filter: grayscale(.6)`. A opacidade
 * foi trocada por uma TINTA ESMAECIDA (`--nx-stale-value-ink`). A divergência é
 * deliberada, está registrada aqui e em `tokens.css`, e a mudança da regra foi
 * levada ao usuário pelo PM.
 *
 * POR QUE: nenhum teste de PAR DE TOKENS enxerga composição. Os seis pares
 * medidos na rodada anterior estavam corretos e a tela reprovava de todo jeito,
 * porque `opacity` compõe a tinta com o fundo DEPOIS de o par ser escolhido.
 * Medido, com `grayscale` em sRGB e a composição de `opacity`:
 *
 *     valor SOC/VEL/ODÔM em linha stale, escuro   par 8,89  →  3,16   (limite 4,5)
 *     idem, claro                                 par 5,93  →  2,51   (limite 4,5)
 *     barra de SOC do CCO                         par 5,03  →  2,03   (limite 3,0)
 *
 * Eram 16 das 18 linhas. E o mecanismo era CONHECIDO: o comentário de
 * `CampoCelula`, logo abaixo, dizia "Medido: ~3,2:1 no escuro depois da
 * composição" — e a medida foi usada para salvar o SUFIXO, deixando o VALOR em
 * 3,16. WCAG 1.4.3 não isenta texto deliberadamente esmaecido, e na célula o
 * número não tem outro portador: se ele não é legível, a célula não informa.
 *
 * O DESENHO CONTINUA DIZENDO "ISTO ESTÁ DEGRADADO": a distância de tinta é o que
 * comunica. `--nx-ink` mede 15,96:1 no claro; `--nx-stale-value-ink` mede
 * 5,76:1 depois do `grayscale` — visivelmente apagado ao lado de um valor
 * corrente, e acima do limite. As razões estão medidas em
 * `styles/contrast.test.ts`, que agora aplica a MATRIZ DE `grayscale` e a
 * composição de `opacity` antes de comparar.
 *
 * `grayscale(0.6)` continua aplicado, e sobre uma tinta quase neutra ele é quase
 * um no-op de propósito: quem degrada agora é a TINTA, e o filtro fica para o
 * conteúdo NÃO-TEXTUAL (a barra de SOC do CCO), onde continua fazendo trabalho.
 */
const ESTILO_DEGRADADO = {
  color: 'var(--nx-stale-value-ink)',
  filter: 'grayscale(0.6)',
} as const;

/**
 * A DEGRADAÇÃO DE UM PORTADOR GRÁFICO — barra, pin, faixa.
 *
 * Existe porque o `CcoPage` aplicava `opacity: .55` no `<td>` INTEIRO, que é a
 * forma que `CampoCelula` abandonou, e ali o portador do SOC é uma BARRA: não há
 * texto para carregar o dado, então a barra é o dado. Composta, ela media 2,03:1
 * contra o próprio trilho — abaixo do limite de 3:1 de conteúdo não-textual
 * (WCAG 1.4.11).
 *
 * `background` e não `color`, porque quem pinta a barra é o preenchimento. O
 * `grayscale` fica pelo mesmo motivo do irmão acima, e aqui ele não é no-op: um
 * portador gráfico pode ter gradiente ou ícone dentro.
 *
 * Medido: `--nx-stale-value-ink` sobre `--nx-bg-sunken` (o trilho) = 5,05:1 no
 * claro e 6,31:1 no escuro, contra 2,03:1 da composição anterior.
 */
export const ESTILO_DEGRADADO_GRAFICO = {
  background: 'var(--nx-stale-value-ink)',
  filter: 'grayscale(0.6)',
} as const;

/**
 * A degradação de TEXTO, exportável — para quem não usa `CampoCelula`.
 *
 * O `CcoPage` monta as células dele à mão (a linha do CCO não é
 * `CelulaDeCampo`), e era ali que a constante estava COPIADA como literal em
 * três lugares. Uma regra de uma linha copiada em três lugares é a taxa de erro
 * conhecida deste projeto — e os três estavam na forma errada.
 */
export const ESTILO_DEGRADADO_TEXTO = ESTILO_DEGRADADO;

/**
 * Uma célula de campo.
 *
 * `formatar` recebe o valor bruto e devolve o texto com unidade. Só é chamado
 * quando `celula.mostraValor` — é o que impede `"0,0 km"` de aparecer por
 * ausência, porque não há valor a formatar quando o estado não autoriza.
 */
export function CampoCelula({
  celula,
  formatar,
  className,
}: {
  celula: CelulaDeCampo;
  formatar: (valor: number | boolean | string) => string;
  className?: string;
}) {
  const cor = COR_DO_TOM[celula.tom];
  /*
    A DEGRADAÇÃO VAI NO VALOR, NÃO NA CÉLULA.

    Era `style={degradado ? ESTILO_DEGRADADO : undefined}` no `<td>` inteiro, e
    isso arrastava o sufixo "(último)" junto: `opacity: .55` mais
    `grayscale(.6)` sobre a ressalva que EXISTE para substituir a cor. Medido:
    ~3,2:1 no escuro depois da composição — o portador não-cromático do padrão
    STALE reprovando, nos dois temas.

    O valor degradado é o ponto do padrão (o número não é de agora); a RESSALVA
    que explica isso tem de ficar legível, senão o operador vê um número
    apagado e nenhuma palavra dizendo por quê.

    E O QUE A MEDIDA ACIMA NÃO RESOLVEU, na rodada seguinte: ela salvou o
    SUFIXO e deixou o VALOR em 3,16:1. A mesma composição que reprovava a
    ressalva reprovava o número, em 16 das 18 linhas, e o número não tem outro
    portador na célula. `ESTILO_DEGRADADO` deixou de usar `opacity`; ver o
    cabeçalho dele.
  */
  return (
    <td className={className} title={celula.frase}>
      <span
        style={{
          color: celula.mostraValor ? cor : 'var(--nx-mute)',
          ...(celula.degradado ? ESTILO_DEGRADADO : {}),
        }}
      >
        {celula.mostraValor && celula.valor !== null ? (
          formatar(celula.valor)
        ) : (
          /* O travessão é `aria-hidden` e a frase vai no `aria-label` do span:
             um leitor de tela anunciando "travessão" não diz por que o dado
             não está lá, e é justamente a distinção entre ausência,
             vencimento e impossibilidade que esta entrega existe para dar. */
          <span aria-hidden="true">—</span>
        )}
        {!celula.mostraValor && <span className="nx-sr-only">{celula.frase}</span>}
      </span>
      <SufixoDeRessalva sufixo={celula.sufixo} cor={cor} />
    </td>
  );
}

/**
 * O SUFIXO DE RESSALVA — `(último)`, `(não fornecido)`, `(carimbo à frente)`.
 *
 * ---------------------------------------------------------------------------
 * UM COMPONENTE, E NÃO SEIS `<span>` IGUAIS, por causa do ESPAÇO.
 *
 * O parêntese precisa de um espaço LITERAL antes dele. `marginLeft` resolve o
 * visual e não resolve o anúncio: o leitor de tela lê `60%(último)` como uma
 * palavra só. Medido na tela, em quatro lugares que tinham `marginLeft` e não
 * tinham o espaço — Central de Operações, fila do Manobrista e as duas células
 * do modal de detalhe —, enquanto a célula da tabela e o KPI já o tinham.
 *
 * Seis cópias de uma regra de uma linha é a forma exata de metade delas
 * esquecerem. Com um componente, esquecer deixa de ser possível, e há guarda de
 * texto proibindo o `({sufixo})` escrito à mão nas telas.
 *
 * `.nx-stale-suffix` é o que mantém o sufixo FORA da degradação de opacidade do
 * padrão STALE: ele é o portador NÃO-CROMÁTICO da ressalva, e degradar o
 * portador que substitui a cor é desfazer o padrão.
 */
export function SufixoDeRessalva({
  sufixo,
  cor,
  tamanho = '.68rem',
}: {
  /** `null` não renderiza nada — é o estado em que não há ressalva a fazer. */
  sufixo: string | null;
  /** `undefined` herda a cor do texto ao redor. */
  cor?: string;
  tamanho?: string;
}) {
  if (sufixo === null) return null;
  return (
    <span
      className="nx-stale-suffix"
      style={{
        fontSize: tamanho,
        fontWeight: 400,
        whiteSpace: 'nowrap',
        ...(cor === undefined ? {} : { color: cor }),
      }}
    >
      {/* O espaço é LITERAL e é o ponto deste componente. */}
      {' '}
      ({sufixo})
    </span>
  );
}

/**
 * O par tinta/fundo de cada estado, por TOKEN SEMÂNTICO.
 *
 * Era paleta crua (`--nx-neutral-100`, `--nx-navy-500`) com fundo
 * `color-mix(... 13%, transparent)`, e reprovou contraste em quatro badges —
 * dois deles porque o par NÃO virava com o tema: "Parado" ia de 12,66:1 no
 * claro para 1,25:1 no escuro sem ninguém mexer em nada.
 *
 * Duas regras que os tokens novos impõem:
 *  - tinta `-600` no claro, não `-500`. Os `-500` são de PREENCHIMENTO, e a
 *    tabela de `--nx-band-*-ink` no `tokens.css` já dizia isso.
 *  - fundo SÓLIDO, não translúcido. Com `color-mix` sobre transparente a razão
 *    real depende do que está atrás, e o que está atrás muda por dobra — card,
 *    linha zebrada, e o popup do Leaflet, que é branco nos dois temas.
 *
 * As razões estão medidas em `styles/contrast.test.ts`, que roda nos dois
 * temas e falha o CI abaixo de 4,5:1.
 */
const COR_DO_MOVIMENTO: Record<EstadoDeMovimento, { fundo: string; tinta: string }> = {
  'sem-leitura': {
    fundo: 'var(--nx-state-unknown-bg)',
    tinta: 'var(--nx-state-unknown-ink)',
  },
  /* Carimbo à frente é PERIGO: o dado aparenta ser o mais fresco de todos. */
  'carimbo-futuro': { fundo: 'var(--nx-state-clock-bg)', tinta: 'var(--nx-state-clock-ink)' },
  'sem-sinal': { fundo: 'var(--nx-state-stale-bg)', tinta: 'var(--nx-state-stale-ink)' },
  carregando: { fundo: 'var(--nx-state-charge-bg)', tinta: 'var(--nx-state-charge-ink)' },
  rodando: { fundo: 'var(--nx-state-live-bg)', tinta: 'var(--nx-state-live-ink)' },
  parado: { fundo: 'var(--nx-state-idle-bg)', tinta: 'var(--nx-state-idle-ink)' },
  /* Indeterminado é NEUTRO e não verde: a leitura é corrente, mas a tela não
     sabe o estado — pintar de qualquer cor de estado seria afirmar um. Divide o
     par com `sem-leitura` de propósito: os dois dizem "não sei", e a diferença
     entre eles está no TEXTO, que é o portador. */
  indeterminado: {
    fundo: 'var(--nx-state-unknown-bg)',
    tinta: 'var(--nx-state-unknown-ink)',
  },
};

/** O badge de estado. O texto é o portador; a cor é reforço (WCAG 1.4.1). */
export function BadgeDeMovimento({
  estado,
  frase,
}: {
  estado: EstadoDeMovimento;
  frase: string;
}) {
  const { fundo, tinta } = COR_DO_MOVIMENTO[estado];
  return (
    <span className="nx-badge" style={{ background: fundo, color: tinta }} title={frase}>
      {ROTULO_DO_MOVIMENTO[estado]}
    </span>
  );
}

/**
 * O card de KPI das telas de frota.
 *
 * POR QUE NÃO É O `.nx-kpi` PURO: `.nx-kpi` é
 * `grid-template-columns: auto 1fr`, desenhado para um ícone ocupando a coluna
 * 1 em duas linhas — e `.nx-kpi__value` e `.nx-kpi__hint` carregam
 * `grid-column: 2` CRAVADO no `global.css`. Sem ícone, o rótulo cai sozinho na
 * coluna `auto` e o valor e a dica ficam espremidos no que sobra.
 *
 * Medido na validação em navegador: card de 151 px com a dica renderizando numa
 * coluna de 44 px e 214 px de altura, ilegível. E `grid-template-columns: 1fr`
 * inline NÃO resolve — o `grid-column: 2` dos filhos cria a segunda coluna
 * implicitamente de qualquer jeito. Foi preciso reposicionar os FILHOS.
 *
 * As dicas desta entrega são longas POR NECESSIDADE: é nelas que moram o "M de
 * N" e a causa da ausência, que são o produto desta fase. Encurtá-las para
 * caber num layout de ícone seria desfazer o trabalho.
 *
 * A correção é INLINE de propósito: mudar `.nx-kpi__value` no `global.css`
 * mexeria em todos os KPIs do portal, inclusive os que dependem da coluna 2.
 */
const OCUPA_A_LINHA = { gridColumn: '1 / -1' } as const;
export function KpiDeFrota({
  label,
  valor,
  hint,
  tone = 'navy',
  cor,
  degradado = false,
  sufixo,
}: {
  label: string;
  /** `null` imprime travessão. Nunca "0" por ausência. */
  valor: string | null;
  hint: string;
  tone?: 'navy' | 'success' | 'orange' | 'cyan';
  cor?: string;
  degradado?: boolean;
  sufixo?: string;
}) {
  return (
    <div
      className={`nx-kpi nx-kpi--${tone}`}
      style={{ gridTemplateColumns: '1fr', gap: '6px' }}
    >
      <div className="nx-kpi__label" style={OCUPA_A_LINHA}>
        {label}
      </div>
      <div className="nx-kpi__value" style={OCUPA_A_LINHA}>
        <span
          style={{
            ...(cor ? { color: cor } : {}),
            ...(degradado ? ESTILO_DEGRADADO : {}),
          }}
        >
        {valor ?? <span aria-hidden="true">—</span>}
        </span>
        {/* O sufixo do KPI já vem entre parênteses do chamador (`(inclui
            último)`), então o componente receberia parêntese duplo. O que se
            reaproveita aqui é a REGRA — o espaço literal —, e ela está escrita
            uma vez só, em `SufixoDeRessalva`. */}
        {sufixo !== undefined && (
          <>
            {' '}
            <span
              className="nx-stale-suffix"
              style={{ fontSize: '.55em', fontWeight: 400, color: 'var(--nx-stale-ink)' }}
            >
              {sufixo}
            </span>
          </>
        )}
      </div>
      <div className="nx-kpi__hint" style={OCUPA_A_LINHA}>
        {hint}
      </div>
    </div>
  );
}

/**
 * A nota que presta contas do que a fonte não fornece.
 *
 * `role="note"` e não `role="status"`: não é atualização de estado, é uma
 * propriedade permanente da fonte configurada. `status` a anunciaria a cada
 * polling de 15 s.
 */
export function NotaDeFonte({ children }: { children: ReactNode }) {
  return (
    <p
      role="note"
      style={{
        margin: '0 0 var(--nx-space-3)',
        padding: '.55rem .75rem',
        borderLeft: '3px solid var(--nx-line-strong)',
        background: 'var(--nx-bg-sunken)',
        color: 'var(--nx-mute-strong)',
        fontSize: '.8rem',
        borderRadius: 'var(--nx-radius-xs)',
      }}
    >
      {children}
    </p>
  );
}

/**
 * Banner de INCONSISTÊNCIA DE FONTE — a última célula da matriz dos dois eixos.
 *
 * `role="alert"` porque é bug de dado em produção, não estado operacional: a
 * fonte declara não fornecer o campo e o banco trouxe valor. Engolir a
 * contradição é como `msg_count = 1` sobreviveu quatro semanas.
 */
export function AvisoDeInconsistencia({
  campos,
  fonte,
}: {
  campos: readonly CampoDeVeiculo[];
  fonte: string;
}) {
  if (campos.length === 0) return null;
  /* `.nome`, e a ausência dele imprimia `[object Object], [object Object]`.
     `ROTULO_DO_CAMPO` deixou de ser `Record<CampoDeVeiculo, string>` e passou a
     ser `{ nome, genero }` para as frases concordarem em gênero; `tsc` fica
     limpo porque `Grandeza[].join(', ')` é chamada válida, e a falha sai só na
     tela — neste banner, que é justamente o que diz ao operador QUAL coluna
     está contraditória. Há guarda de texto varrendo a classe. */
  const nomes = campos.map((c) => ROTULO_DO_CAMPO[c].nome).join(', ');
  return (
    <p
      role="alert"
      style={{
        margin: '0 0 var(--nx-space-3)',
        padding: '.55rem .75rem',
        borderLeft: '3px solid var(--nx-alert-ink)',
        background: 'var(--nx-state-clock-bg)',
        color: 'var(--nx-alert-ink)',
        fontSize: '.8rem',
        borderRadius: 'var(--nx-radius-xs)',
      }}
    >
      {/*
        Os espaços em torno do `<code>` são LITERAIS, e não só margem de CSS: o
        leitor de tela concatena os nós de texto, e sem eles a frase sai como
        "Verifiquetelemetry_sources.unsupported_fieldse o serviço". Margem
        resolve o visual e não resolve o anúncio.
      */}
      Inconsistência de fonte: {fonte} declara não fornecer {nomes}, e a leitura trouxe valor.
      Estes números não são confiáveis e foram omitidos dos indicadores. Verifique{' '}
      <code>telemetry_sources.unsupported_fields</code> e o serviço de sincronia.
    </p>
  );
}

/**
 * Estado vazio de um RECURSO que a fonte ativa não sustenta.
 *
 * `role="note"`, não `role="alert"`: não há nada a corrigir. E a frase vem
 * pronta de `fraseDeRecursoNaoSuportado`, sem "ainda" — o advérbio promete que
 * esperar resolve, e não resolve.
 */
export function RecursoSemFonte({
  titulo,
  frase,
  altura = 320,
  children,
}: {
  titulo: string;
  frase: string;
  altura?: number;
  children?: ReactNode;
}) {
  return (
    <div
      role="note"
      style={{
        minHeight: altura,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        textAlign: 'center',
        gap: '.6rem',
      }}
    >
      <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--nx-ink)' }}>{titulo}</div>
      <p style={{ margin: 0, fontSize: '.85rem', maxWidth: 560, color: 'var(--nx-mute)' }}>
        {frase}
      </p>
      {children}
    </div>
  );
}
